import { deepCompare } from '@/_utils/deep-compare';
import { removeDuplicatesFromArray } from '@/_utils/remove-duplicates-from-array';
import { DetectValueChangeOnFocusOutDirective } from '@/common/detect-value-change-on-focus-out/detect-value-change-on-focus-out.directive';
import { OfferTransportKind } from '@/common/enums/OfferTransportKind';
import { PricePipe } from '@/common/pipes/price.pipe';
import {
	StopConnection,
	StopKindColor,
	TimetableCity,
	TimetableWithNestedAntennas,
	TransportLogic,
	TransportTableDataSource,
	TransportTimetable,
	TransportVehicleKind,
	VehicleInGroupedTimetable,
} from '@/new-transport/_models/transport-timetable.model';
import {
	PhpPassengersData,
	StopKind,
	TransportPassengers,
	TransportSide,
} from '@/new-transport/_models/transport.model';
import { DateTimeFormComponent } from '@/new-transport/transport-timetable/date-time-form/date-time-form.component';
import { BoardingStatusDirective } from '@/new-transport/transport-timetable/transport-timetable-table/boarding-status.directive';
import { StopConnectionModalComponent } from '@/new-transport/transport-timetable/transport-timetable-table/stop-connection-modal/stop-connection-modal.component';
import { ExpansionPanelOpenState } from '@/new-transport/transport-timetable/transport-timetable.component';
import { NgStyle, NgTemplateOutlet } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	Injector,
	OnInit,
	computed,
	inject,
	input,
	output,
	signal,
	viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import {
	MatAccordion,
	MatExpansionPanel,
	MatExpansionPanelContent,
	MatExpansionPanelDescription,
	MatExpansionPanelHeader,
	MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTable, MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { finalize, combineLatest, filter, forkJoin, map, Observable, take } from 'rxjs';
import {
	TransportEventModalData,
	TransportEventVehicle,
} from '@/new-transport/transport-timetable/transport-event/transport-event.model';
import { CreateNewTransportEventModalComponent } from '@/new-transport/transport-timetable/transport-event/create-new-modal/create-new-transport-event-modal.component';
import {
	ResourceName,
	TransportEventApiService,
} from '@/new-transport/transport-timetable/_api/transport-event.api.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { TransportApiService } from '@/new-transport/_api/transport.api.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
	selector: 'app-transport-timetable-table',
	standalone: true,
	imports: [
		DateTimeFormComponent,
		MatButtonModule,
		MatSlideToggle,
		MatTableModule,
		FormsModule,
		ReactiveFormsModule,
		MatSelectModule,
		MatInput,
		PricePipe,
		NgStyle,
		BoardingStatusDirective,
		MatAccordion,
		MatExpansionPanel,
		MatExpansionPanelContent,
		MatExpansionPanelDescription,
		MatExpansionPanelHeader,
		MatExpansionPanelTitle,
		MatTooltip,
		NgTemplateOutlet,
		DetectValueChangeOnFocusOutDirective,
		MatFormFieldModule,
		MatInputModule,
		MatAutocompleteModule,
		MatProgressSpinnerModule,
	],
	templateUrl: './transport-timetable-table.component.html',
	styleUrl: './transport-timetable-table.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransportTimetableTableComponent implements OnInit {
	// inputs
	readonly timetables = input.required<TransportTimetable[]>();
	readonly transportLogic = input.required<TransportLogic[]>();
	readonly antennas = input<TimetableWithNestedAntennas[]>([]);
	readonly timeDepartureMap = input.required<Map<number, string[]>>();
	readonly timeDepartures = input.required<string[]>();
	readonly vehicleId = input.required<number>();
	readonly isExpandedPanelsArr = input.required<ExpansionPanelOpenState[]>();
	readonly transportKind = input.required<OfferTransportKind>();
	readonly filteredOptions = input.required<TransportLogic['vehicle'][]>();
	readonly side = input.required<TransportSide>();
	readonly transportId = input.required<number>();

	// outputs
	readonly timeDepartureChange = output<{ timeDeparture: string; id: number; vehicleId: number }>();
	readonly stopConnectionChange = output<StopConnection>();
	readonly stopSaleChange = output<{ stopSale: boolean; timetableId: number; vehicleId: number }>();
	readonly feeChange = output<{ fee: number; timetableId: number; vehicleId: number }>();
	readonly flightNumberChange = output<{ flightNumber: string; timetableId: number; vehicleId: number }>();
	readonly pauseDurationChange = output<{ pauseDuration: number; timetableId: number; vehicleId: number }>();
	readonly expansionStateChange = output<{ expansionPanel: ExpansionPanelOpenState; valueToSet: boolean }>();
	readonly filterVehiclesByNameChange = output<{ value: string | TransportLogic['vehicle'] }>();
	readonly changeSelectedVehicleChange = output<{
		currentVehicle: VehicleInGroupedTimetable;
		vehicleIdToUpdate: number;
	}>();

	readonly dialog = inject(MatDialog);
	readonly infoboxService = inject(InfoboxService);
	readonly symfonyApiService = inject(TransportEventApiService);

	// enums
	readonly StopKind = StopKind;
	readonly TransportVehicleKind = TransportVehicleKind;
	readonly StopKindColor = StopKindColor;
	readonly OfferTransportKind = OfferTransportKind;

	readonly antennasLoader = signal(false);

	readonly displayedColumns = computed(() => {
		return this.transportKind() === OfferTransportKind.PLANE
			? ['city', 'slotsCount', 'stopSale', 'surcharge', 'flightNumber', 'airline', 'departure']
			: ['city', 'slotsCount', 'stopSale', 'surcharge', 'break', 'departure', 'time'];
	});

	dataSource: TransportTableDataSource[] = [];

	private readonly table = viewChild(MatTable);

	private readonly injector = inject(Injector);
	private readonly destroyRef = inject(DestroyRef);
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly transportApiService = inject(TransportApiService);

	ngOnInit(): void {
		combineLatest([
			toObservable(this.timetables, { injector: this.injector }),
			toObservable(this.transportLogic, { injector: this.injector }),
		])
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(([timetables, transportLogic]) => {
				const newData = this.generateData(timetables, transportLogic);
				this.setDataSource(newData);
				this.table().renderRows();
				this.cdr.markForCheck();
			});
	}

	emitTimeDeparture(timeDeparture: string, id: number, vehicleId: number): void {
		this.timeDepartureChange.emit({ timeDeparture, id, vehicleId });
	}

	emitStopConnection(stopConnection: StopConnection): void {
		this.stopConnectionChange.emit(stopConnection);
	}

	emitStopSale(stopSale: boolean, timetableId: number, vehicleId: number): void {
		this.stopSaleChange.emit({ stopSale, timetableId, vehicleId });
	}

	emitFee(fee: number, timetableId: number, vehicleId: number): void {
		this.feeChange.emit({ fee, timetableId, vehicleId });
	}

	emitFlightNumber(flightNumber: string, timetableId: number, vehicleId: number): void {
		this.flightNumberChange.emit({ flightNumber, timetableId, vehicleId });
	}

	emitPauseDuration(pauseDuration: number, timetableId: number, vehicleId: number): void {
		this.pauseDurationChange.emit({ pauseDuration, timetableId, vehicleId });
	}

	emitFilterVehicleByName(value: string | TransportLogic['vehicle']): void {
		this.filterVehiclesByNameChange.emit({ value });
	}

	emitChangeSelectedVehicle(currentVehicle: VehicleInGroupedTimetable, vehicleIdToUpdate: number): void {
		this.changeSelectedVehicleChange.emit({ currentVehicle, vehicleIdToUpdate });
	}

	displayFn(user: TransportLogic['vehicle']): string {
		return user && user.name ? user.name : '';
	}

	showStopConnectionDetails(stopConnection: StopConnection, index: number): void {
		this.dialog
			.open<
				StopConnectionModalComponent,
				{ stopConnection: StopConnection; stopFrom: TimetableCity; stopTo: TimetableCity }
			>(StopConnectionModalComponent, {
				minWidth: '40%',
				data: {
					stopConnection: stopConnection ?? null,
					stopFrom: this.dataSource[index].stop,
					stopTo: this.dataSource[index + 1].stop,
				},
			})
			.afterClosed()
			.pipe(filter((v) => !!v))
			.subscribe((stopConnection) => {
				this.emitStopConnection(stopConnection);
			});
	}

	openTourismEventModal(item: TimetableWithNestedAntennas): void {
		this.antennasLoader.set(true);
		const { timetable, vehicle } = item;

		if (timetable.length < 1) {
			this.antennasLoader.set(false);
			this.infoboxService.warning('W rozkładzie brakuje wyjazdów, by móc utworzyć antenkę.');
			return;
		}

		const transportReq$ = this.transportApiService.getTransportPassengers(this.transportId()).pipe(
			filter(Boolean),
			map((data) => this.mapPassengers(data, item.vehicle.id)),
		);

		forkJoin({
			transportPassengers: transportReq$,
			vehicle: this.getPhpVehicle(vehicle.name),
		})
			.pipe(
				filter((result) => !!result.transportPassengers && !!result.vehicle),
				finalize(() => this.antennasLoader.set(false)),
			)
			.subscribe(({ transportPassengers, vehicle }) => {
				const modalData: TransportEventModalData = {
					passengers: transportPassengers,
					vehicle: vehicle,
					side: this.side(),
				};

				this.dialog.open<CreateNewTransportEventModalComponent, TransportEventModalData>(
					CreateNewTransportEventModalComponent,
					{
						minWidth: '500px',
						data: modalData,
					},
				);
			});
	}

	private setDataSource(data: TransportTableDataSource[]): void {
		// if length same - we update only changed rows
		if (this.dataSource.length > 0 && this.dataSource.length === data.length) {
			data.forEach((data, index) => {
				if (!deepCompare(data, this.dataSource[index])) {
					this.dataSource[index] = data;
				}
			});
			return;
		}

		this.dataSource = data;
	}

	private generateData(
		timetables: TransportTimetable[],
		transportLogic: TransportLogic[],
	): TransportTableDataSource[] {
		return timetables.map((timetable, index) => {
			const stopId = timetable.stop.id;

			const actions = removeDuplicatesFromArray(
				transportLogic.filter((logic) => logic.city === stopId).map((logic) => logic.action),
			);

			const isNotLastRow = index < timetables.length - 1;

			const nextTimetable = timetables[index + 1];
			const nextActions = transportLogic
				.filter((logic) => logic.city === nextTimetable?.stop.id)
				.map((logic) => logic.action);

			const isNextTarget = nextActions.toString() === StopKind.TARGET.toString();
			const isNotBeforeFinalTargetRow = index < timetables.length - 2 || !isNextTarget;

			return {
				...timetable,
				action: actions,
				slotsCount: {
					boarding: this.findSlotsCountByStopKind(stopId, StopKind.BOARDING),
					dropOff: this.findSlotsCountByStopKind(stopId, StopKind.DROP_OFF),
				},
				isNotLastRow,
				isNotBeforeFinalTargetRow,
			};
		});
	}

	private findSlotsCountByStopKind(stopId: number, stopKind: StopKind): number | null {
		const slots = this.transportLogic()
			.filter((logic) => logic.city === stopId && logic.action === stopKind)
			.flatMap((logic) => logic.reservationOfferSlots || []);

		return slots.length > 0 ? slots.length : null;
	}

	private mapPassengers(vehiclesData: TransportPassengers, vehicleId: number): PhpPassengersData[] {
		return vehiclesData.passengersInLogic
			.filter((v) => v.vehicle.id === vehicleId)
			.map((v) => ({
				address: v.address,
				city: v.city,
				time: v.timeDeparture,
				slots: {
					confirmed: v.totalPassengers.confirmed,
					optional: v.totalPassengers.optional,
				},
			}));
	}

	private getPhpVehicle(vehicleName: string): Observable<TransportEventVehicle> {
		return this.symfonyApiService.search(ResourceName.VEHICLES, vehicleName.toString()).pipe(
			map((vehicles: TransportEventVehicle[]) => {
				if (vehicles.length < 1) {
					this.infoboxService.warning(
						`Nie można przypisać wirtualnego pojazdu ${vehicleName}. Upewnij się, że jest wybrany poprawny pojazd.`,
					);
					return null;
				}
				return vehicles[0];
			}),
		);
	}
}
